/**
 * Snapshot Capture — Serializes the live DOM with PII redaction.
 *
 * Used by the visual editor to let an authenticated admin capture an auth page
 * so a translator (without credentials) can later translate it against a
 * frozen copy. The serialized HTML is uploaded to the server and retrieved in
 * viewer mode (?__lr_snapshot={uuid}) where Inspector + Sidebar boot against
 * the snapshot root instead of document.body.
 *
 * Redaction strategy:
 *   - Remove: <script>, <noscript>, <iframe>, <embed>, <object>, editor host
 *   - Clear all form <input>, <textarea>, <select> values
 *   - Replace [data-pii] / [data-sensitive] text with [REDACTED]
 *   - Strip all inline event handlers (onclick, onmouseover, etc.)
 *   - Inline PII redaction in text nodes (email / phone / URL / SSN /
 *     credit card / IPv4) — see pii-redactor.ts. `[data-no-redact]`
 *     subtrees, `<code>`, and `<pre>` are skipped.
 *   - Strip CSRF/CSP meta tags and auth tokens from link/script URLs
 */

import { BlockExtractor } from '../block-extractor.js';
import { redactTextNodes } from './pii-redactor.js';

const REDACTED = '[REDACTED]';

export interface SnapshotTextEntry {
  /** auto_xxxxxxxxxxxx — 12-char md5 hash, generated BEFORE redaction so it matches the live page */
  key: string;
  /** The source tag name (P, H1, LI, etc.) — useful for future analytics */
  tagName: string;
  /** Index of this block among all block elements in the snapshot DOM. Used by
   *  the viewer to map a clicked element back to its pre-computed key without
   *  re-running BlockExtractor on the redacted text. */
  blockIndex: number;
}

export interface SnapshotViewport {
  width: number;
  height: number;
  devicePixelRatio: number;
}

export interface CapturedSnapshot {
  html: string;
  textEntries: SnapshotTextEntry[];
  pageUrl: string;
  title: string;
  viewport: SnapshotViewport;
}

/**
 * Capture the current page as a redacted snapshot.
 *
 * Pure function — does NOT mutate the live DOM. Clones first, then:
 *   1. Extracts block keys BEFORE redaction (so auto_keys match the live page)
 *   2. Redacts the clone (strips PII, scripts, forms, etc.)
 *   3. Stamps each block element with a data-lr-snap-idx attribute so the
 *      viewer can map a click back to the pre-computed key
 *   4. Serializes to HTML
 *
 * The textEntries carry pre-computed keys (no source text) and the HTML is
 * safe to store. The viewer uses textEntries to look up the correct key by
 * index when the translator clicks a block.
 */
export function captureSnapshot(): CapturedSnapshot {
  // 1. Clone the entire document element.
  const clone = document.documentElement.cloneNode(true) as HTMLElement;

  // 2. Extract blocks BEFORE redaction so keys match the live page.
  //    The real text is used for md5 hashing, producing the same auto_keys
  //    the edge proxy / auto-init generates on the live page.
  const textEntries = extractTextEntries(clone);

  // 3. Stamp each extracted block with a data attribute linking it to its
  //    entry index. The viewer uses this to resolve clicks to pre-computed
  //    keys without re-running BlockExtractor on the redacted text.
  //    (extractTextEntries stores the element reference so we can stamp it)

  // 4. Redact the clone. Order: remove dangerous elements first so we don't
  //    waste time walking their subtrees.
  removeDangerousElements(clone);
  clearFormValues(clone);
  redactSensitiveElements(clone);
  stripInlineEventHandlers(clone);
  redactTextNodePII(clone);
  stripSensitiveMetaAndHeaders(clone);

  // 5. Serialize.
  const html = '<!DOCTYPE html>\n' + clone.outerHTML;

  return {
    html,
    textEntries,
    pageUrl: window.location.href,
    title: document.title,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
    },
  };
}

/**
 * Remove elements that should never appear in a snapshot:
 * - <script>, <noscript>: could re-execute or leak source
 * - <iframe>, <embed>, <object>: third-party content, likely auth-bound
 * - #__lionrapid-editor: the editor's own shadow host
 * - <link rel="prefetch"> / <link rel="preload"> that may leak auth URLs
 */
function removeDangerousElements(root: HTMLElement): void {
  const selectors = [
    'script',
    'noscript',
    'iframe',
    'embed',
    'object',
    '#__lionrapid-editor',
    'link[rel="prefetch"]',
    'link[rel="preload"]',
    'link[rel="modulepreload"]',
  ];
  for (const selector of selectors) {
    const matches = root.querySelectorAll(selector);
    for (let i = 0; i < matches.length; i++) {
      matches[i].remove();
    }
  }
}

/**
 * Clear all form field values so no user input leaks.
 * This covers <input> (all types), <textarea>, and <select>.
 */
function clearFormValues(root: HTMLElement): void {
  const inputs = root.querySelectorAll('input, textarea, select');
  for (let i = 0; i < inputs.length; i++) {
    const el = inputs[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

    // Clear live value
    if ('value' in el) el.value = '';

    // Clear the serialized attribute — cloneNode copies the attribute,
    // not the .value property, so we need to touch both.
    if (el.hasAttribute('value')) el.setAttribute('value', '');

    // <textarea> content lives in text children, not an attribute
    if (el.tagName === 'TEXTAREA') el.textContent = '';

    // Clear option selection on <select>
    if (el.tagName === 'SELECT') {
      const options = el.querySelectorAll('option');
      for (let j = 0; j < options.length; j++) {
        (options[j] as HTMLOptionElement).selected = false;
        options[j].removeAttribute('selected');
      }
    }

    // Remove data-* attributes that often hold state
    const attrs = Array.from(el.attributes);
    for (const attr of attrs) {
      if (attr.name.startsWith('data-value') || attr.name === 'data-initial') {
        el.removeAttribute(attr.name);
      }
    }
  }
}

/**
 * Replace the text content of elements flagged as sensitive with [REDACTED].
 * Preserves the element itself so layout stays intact.
 */
function redactSensitiveElements(root: HTMLElement): void {
  const sensitive = root.querySelectorAll('[data-pii], [data-sensitive]');
  for (let i = 0; i < sensitive.length; i++) {
    const el = sensitive[i] as HTMLElement;
    el.textContent = REDACTED;
    // Strip all attributes except structural ones so classes/ids still position it.
    const attrs = Array.from(el.attributes);
    for (const attr of attrs) {
      if (attr.name !== 'class' && attr.name !== 'id' && attr.name !== 'style') {
        el.removeAttribute(attr.name);
      }
    }
  }
}

/**
 * Walk every element and strip any attribute that starts with 'on' (onclick,
 * onmouseover, onload, etc.). These could re-execute in the viewer.
 */
function stripInlineEventHandlers(root: HTMLElement): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
  let node = walker.nextNode() as HTMLElement | null;
  while (node) {
    const attrs = Array.from(node.attributes);
    for (const attr of attrs) {
      if (attr.name.toLowerCase().startsWith('on')) {
        node.removeAttribute(attr.name);
      }
    }
    node = walker.nextNode() as HTMLElement | null;
  }
}

/**
 * Walk text nodes and replace PII matches inline. Phase 4: matches are
 * replaced where they appear ("Contact foo@bar.com today" →
 * "Contact [REDACTED] today") instead of redacting the whole text node.
 * See pii-redactor.ts for pattern details and the data-no-redact escape
 * hatch.
 */
function redactTextNodePII(root: HTMLElement): void {
  redactTextNodes(root);
}

/**
 * Strip meta tags and link/script URLs that commonly carry auth material.
 */
function stripSensitiveMetaAndHeaders(root: HTMLElement): void {
  // CSRF / CSP nonces
  const sensitiveMetas = root.querySelectorAll(
    'meta[name="csrf-token"], meta[name="csp-nonce"], meta[name="csrf-param"], meta[http-equiv="set-cookie"]',
  );
  for (let i = 0; i < sensitiveMetas.length; i++) {
    sensitiveMetas[i].remove();
  }

  // Strip token-bearing query params from <link> and <a href> URLs
  const TOKEN_PARAMS = ['token', 'auth', 'access_token', 'session', 'sid', 'key', 'api_key', 'apikey'];
  const urlElements = root.querySelectorAll('link[href], a[href], img[src]');
  for (let i = 0; i < urlElements.length; i++) {
    const el = urlElements[i] as HTMLLinkElement | HTMLAnchorElement | HTMLImageElement;
    const attr = el.tagName === 'IMG' ? 'src' : 'href';
    const raw = el.getAttribute(attr);
    if (!raw) continue;
    try {
      const url = new URL(raw, window.location.href);
      let changed = false;
      for (const param of TOKEN_PARAMS) {
        if (url.searchParams.has(param)) {
          url.searchParams.set(param, REDACTED);
          changed = true;
        }
      }
      if (changed) el.setAttribute(attr, url.toString());
    } catch {
      // Not a parseable URL (mailto:, tel:, javascript:, etc.) — skip
    }
  }
}

/**
 * Run BlockExtractor on the clone BEFORE redaction to produce text entries
 * with content-hash keys that match the live page. Each extracted block
 * element is stamped with `data-lr-snap-idx` so the viewer can map a click
 * back to the pre-computed key without re-running BlockExtractor on the
 * (now redacted) text.
 *
 * No source text is included in the returned entries — only the key, tag
 * name, and block index — so the entries are safe to store even though
 * extraction happened before redaction.
 */
function extractTextEntries(clone: HTMLElement): SnapshotTextEntry[] {
  const extractor = new BlockExtractor(
    { emails: true, urls: true, numbers: true, phones: true },
    [],
    2,
  );
  const blocks = extractor.extract(clone);

  return blocks.map((block, index) => {
    // Stamp the element so the viewer can resolve clicks by data attribute
    block.element.setAttribute('data-lr-snap-idx', String(index));

    return {
      key: block.key,
      tagName: block.element.tagName,
      blockIndex: index,
    };
  });
}
